import React, { useState } from 'react';
import { Box, ListItem, ListItemAvatar, Avatar, IconButton, Popover, Typography, useTheme, useMediaQuery } from '@mui/material';
import { MoreVertical } from 'lucide-react';
import { Task, TaskDueDate } from '../../types';
import { TaskStatusIcon } from './TaskStatusIcon';
import { TaskPriorityChip } from './TaskPriorityChip';
import { TaskStatusTransition } from './TaskStatusTransition';
import { users } from '../../data/sampleData';
import { formatRelativeDueDate } from '../../utils/dates';

interface TaskListItemProps {
  task: Task;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, task: Task) => void;
  onStatusChange?: (taskId: string, newStatus: Task['status']) => void;
}

export const TaskListItem: React.FC<TaskListItemProps> = ({ 
  task, 
  onMenuOpen,
  onStatusChange 
}) => {
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const isPastDue = typeof task.dueDate === 'string' && 
    new Date(task.dueDate) < new Date() && 
    task.status !== 'completed';

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusChange = (newStatus: Task['status']) => {
    onStatusChange?.(task.id, newStatus);
    handleStatusClose();
  };

  const formatDueDate = (dueDate: string | TaskDueDate) => {
    if (typeof dueDate === 'string') {
      return `Due: ${new Date(dueDate).toLocaleDateString()}`;
    }
    return formatRelativeDueDate(dueDate);
  };

  return (
    <ListItem
      secondaryAction={
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            ml: 2,
          }}
        >
          {!isMobile && <TaskPriorityChip priority={task.priority} />}
          <IconButton 
            edge="end" 
            aria-label="task menu"
            onClick={(e) => onMenuOpen(e, task)}
          >
            <MoreVertical size={20} />
          </IconButton>
        </Box>
      }
      sx={{
        py: { xs: 2, sm: 1.5 },
        pr: { xs: 6, sm: 12 },
        '& .MuiListItemSecondaryAction-root': {
          right: { xs: 8, sm: 16 },
        },
      }}
    >
      <ListItemAvatar>
        <Avatar 
          sx={{ 
            bgcolor: 'background.default',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            }
          }}
          onClick={handleStatusClick}
        >
          <TaskStatusIcon status={task.status} isPastDue={isPastDue} />
        </Avatar>
      </ListItemAvatar>
      <Box sx={{ 
        flex: 1, 
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1, sm: 0.5 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography 
            sx={{ 
              flex: 1,
              fontWeight: 500,
              color: 'text.primary',
              fontSize: '0.9375rem',
            }}
          >
            {task.title}
          </Typography>
          {isMobile && (
            <TaskPriorityChip priority={task.priority} />
          )}
        </Box>

        <Typography 
          sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.description}
        </Typography>

        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 0.5, sm: 2 },
          mt: { xs: 0.5, sm: 0 },
        }}>
          <Typography
            component="time"
            variant="caption"
            sx={{ 
              color: isPastDue ? 'error.main' : 'text.secondary',
              fontWeight: isPastDue ? 500 : 400,
            }}
          >
            {formatDueDate(task.dueDate)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ 
              color: 'text.secondary',
            }}
          >
            Assigned to: {users.find(u => u.id === task.assignedTo)?.name}
          </Typography>
        </Box>
      </Box>

      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl}
        onClose={handleStatusClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <TaskStatusTransition
          currentStatus={task.status}
          onStatusChange={handleStatusChange}
        />
      </Popover>
    </ListItem>
  );
};